// The console's report layout (spec/13 §2: a sleek platform, not data entry), shared by every Reports tab and the
// day close so they read as one system: a row of headline figures (big numbers, a delta chip, a sparkline), then
// cards of equal height in a grid that fills the width — two to a row on a desktop, one on a phone — each with the
// same header: a 14px title, a one-line description under it in sentence case, and an action on the right.
//
// The charts in the cards are the kit's (`@clavian/ui-web` charts, BRAND.md §4.7). Every piece here is layout around
// figures the server computed.

import type { Money } from '@clavian/api-client';
import { minorUnitDigits } from '@clavian/i18n';
import { useTranslation, type useVenueFormatter } from '@clavian/i18n/react';
import { cx } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { TickingMoney, TickingNumber } from '../../ui/ticking';

import './report-kit.css';

type Format = ReturnType<typeof useVenueFormatter>;

/** A headline amount, as the report's big figures show it: whole rupees drop their paise (₹2,500), others keep them. */
export function headlineMoney(format: Format, value: Money): string {
  const whole = value.amount % 10 ** minorUnitDigits(value.currency) === 0;
  return format.money(value, whole ? 'noFraction' : 'standard');
}

/** A report's body: its figures and cards, evenly spaced. */
export function ReportPage({ children }: { readonly children: ReactNode }): ReactNode {
  return <div className="console-rk-page">{children}</div>;
}

/**
 * The headline figures: StatCards as the console's summary strip (ADR-0043), four (or three) to a row, two at tablet
 * width, one on a phone.
 */
export function ReportKpis({ label, columns = 4, children }: { readonly label?: string; readonly columns?: 3 | 4; readonly children: ReactNode }): ReactNode {
  return (
    <div className={cx('console-summary', 'console-rk-kpis', columns === 3 && 'console-rk-kpis--three')} role={label === undefined ? undefined : 'group'} aria-label={label}>
      {children}
    </div>
  );
}

/**
 * A headline amount that ticks to its value when it changes (a new range, a new day): `headlineMoney`'s rule — whole
 * rupees drop their paise — is decided by the amount it lands on, so the in-between frames keep one width and the last
 * is the exact figure.
 */
export function HeadlineMoney({ format, value }: { readonly format: Format; readonly value: Money }): ReactNode {
  const whole = value.amount % 10 ** minorUnitDigits(value.currency) === 0;
  return <TickingMoney value={value} format={(money) => format.money(money, whole ? 'noFraction' : 'standard')} />;
}

/**
 * A report's headline figure — an amount in minor units, a count, seconds, basis points — that ticks to its value when
 * the range changes, and lands on it exactly; "—" where the period has nothing measured.
 */
export function KpiFigure({ value, format }: { readonly value: number | null | undefined; readonly format: (value: number) => string }): ReactNode {
  if (value === null || value === undefined) return <span className="console-num">—</span>;
  return <TickingNumber className="console-num" value={value} format={format} />;
}

/**
 * Cards side by side, of equal height: `even` halves, `lead` (the first wider: a chart beside its breakdown), or
 * `three` across a wide screen. One column on a narrow screen; a card left alone on a row takes the whole row.
 */
export function ReportGrid({ layout = 'even', children }: { readonly layout?: 'even' | 'lead' | 'three'; readonly children: ReactNode }): ReactNode {
  return <div className={cx('console-rk-grid', `console-rk-grid--${layout}`)}>{children}</div>;
}

/**
 * One card: its title, a one-line description under it, and an optional action on the right (a Days/Weeks switch);
 * `flush` lets a table run edge to edge under the header.
 */
export function ReportCard({
  title,
  description,
  action,
  flush = false,
  className,
  children,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly flush?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <article className={cx('console-rk-card', flush && 'console-rk-card--flush', className)}>
      <header className="console-rk-card__head">
        <div className="console-rk-card__titles">
          <h3 className="console-rk-card__title">{title}</h3>
          {description !== undefined ? <p className="console-rk-card__description">{description}</p> : null}
        </div>
        {action !== undefined ? <div className="console-rk-card__action">{action}</div> : null}
      </header>
      {children}
    </article>
  );
}

/** Label and figure pairs in divided rows; a row may carry a hint under its label. */
export function ReportRows({ children, label }: { readonly children: ReactNode; readonly label?: string }): ReactNode {
  return (
    <dl className="console-rk-rows" aria-label={label}>
      {children}
    </dl>
  );
}

export function ReportRow({
  label,
  hint,
  value,
  quiet = false,
  strong = false,
}: {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly value: ReactNode;
  /** A figure that did not happen (a zero): greyed, so the ones that did stand out. */
  readonly quiet?: boolean;
  /** A total. */
  readonly strong?: boolean;
}): ReactNode {
  return (
    <div className={cx('console-rk-row', quiet && 'console-rk-row--quiet', strong && 'console-rk-row--strong')}>
      <dt>
        <span className="console-rk-row__label">{label}</span>
        {hint !== undefined ? <span className="console-rk-row__hint">{hint}</span> : null}
      </dt>
      <dd className="console-money">{value}</dd>
    </div>
  );
}

/** Small figures in a grid of tiles (voids, refunds, discounts, comps): a label, the figure, a quiet line under it. */
export function ReportTiles({ label, items }: { readonly label?: string; readonly items: readonly { readonly key: string; readonly label: ReactNode; readonly value: ReactNode; readonly hint?: ReactNode }[] }): ReactNode {
  return (
    <dl className="console-rk-tiles" aria-label={label}>
      {items.map((item) => (
        <div key={item.key} className="console-rk-tile">
          <dt className="console-rk-tile__label">{item.label}</dt>
          <dd className="console-rk-tile__value console-money">{item.value}</dd>
          {item.hint !== undefined ? <dd className="console-rk-tile__hint">{item.hint}</dd> : null}
        </div>
      ))}
    </dl>
  );
}

/** A quiet line under a card's figures: what did not happen, or how to read them. */
export function ReportNote({ children }: { readonly children: ReactNode }): ReactNode {
  return <p className="console-rk-note">{children}</p>;
}

/** A step's figure with its sign spelled out, for the eye and for a screen reader ("− ₹45", read "minus ₹45"). */
export function Signed({ sign, amount }: { readonly sign: '+' | '−'; readonly amount: string }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <>
      <span aria-hidden="true">{sign} </span>
      <span className="clav-visually-hidden">{sign === '+' ? t('reports.chart.plus') : t('reports.chart.minus')} </span>
      {amount}
    </>
  );
}
